/**
 * Field names match project-plans/active/2026-09-29-ai-crm/ai-crm-data-model.md.
 *
 * Fields marked "sample only" exist so the screens can look like Wil's handoff while the
 * data is fake. They are not columns in the data model. Phase 2 and later either read the
 * real equivalent (a count, a date, a join) or pass null, and the screen copes with null.
 *
 * - CrmUser.headline, memberSince, summary: `memberSince` is `users.createdAt`. Headline is
 *   `users.title` plus `company.name`, else `users.shortBio`. `summary` is
 *   `tool_member_summaries.body` and stays empty until the L21 retention note exists.
 *   `summaryKind` `stored` is that row. Omitted means the sample sentence.
 * - CrmUser.samplePlanName, samplePriceLabel: desk display of Subscription plan and price.
 * - CrmThread.channelKey: sample only (the plan allows a DM or email chip).
 *   `commentsCount` and `startedAt` are `Thread` fields that exist today.
 * - CrmRule.exampleMessage: nullable `tool_rules.exampleMessage` (L38). Operator-pasted.
 *   The handoff's real member quotes never go in the repo.
 * - CrmCard: topic, good, payoff, steps, and failure are the Phase 6 columns (L37).
 *   `body` is the plain-text render. The Decided / open-question chips are not a column.
 * - isAutoReply on a comment row is derived from the audit (originKey system), as Phase 3 will.
 * - Record variables (first_name, sla_date, resource_links) fill from records at send time,
 *   not from a binding.
 */

export type CrmLaneKey = 'none' | 'auto' | 'assist' | 'human'
export type CrmGroupKey = 'hard-stop' | 'lane'
export type CrmAudienceKey = 'members' | 'responder' | 'sco-admin'
export type CrmDraftOrigin = 'personal' | 'tool'
export type CrmEvaluatorKey = 'code' | 'model'
export type CrmDraftModeKey = 'none' | 'pre-drafted' | 'briefing-first'
/** How the shared words become the message. Missing or unknown reads as literal. */
export type CrmTemplateKind = 'literal' | 'generated'
export type CrmCheckResult = 'won' | 'matched-lost' | 'no-match' | 'not-scored' | 'skipped-no-binding'
export type CrmOutcomeKey =
  'no-match' | 'shadow' | 'silent' | 'drafted' | 'sent' | 'held' | 'superseded' | 'error'
export type CrmChannelKey = 'dm' | 'email'
/** Why a person marked a message "no reply needed". The server keeps the short key. */
export type CrmDismissReason = 'thanks' | 'handled-elsewhere' | 'not-for-us'
export type CrmCardTypeKey = 'method' | 'advice' | 'system' | 'policy'
export type CrmRange = '7d' | '30d' | 'qtd' | 'ytd'

export type CrmUser = {
  userId: string
  displayName: string
  email: string
  crmEnabled: boolean
  responderToolId: string | null
  samplePlanName: string | null
  samplePriceLabel: string | null
  /** Sample only. One line under the name: role and company. */
  headline: string | null
  /** Sample only. `users.createdAt`. */
  memberSince: string | null
  /** Sample only. AI-written summary. `**bold**` marks the phrases to lift. */
  summary: string | null
}

export type CrmTool = {
  toolId: string
  title: string
  slug: string
  audienceKey: CrmAudienceKey
  servesUserId: string | null
  servesConsentAt: string | null
  servesConsentByUserId: string | null
  toolUserId: string | null
  /** Present when the rules client already knows the names. The sample tool rows omit them. */
  personaName?: string | null
  servesName?: string | null
}

export type CrmRule = {
  toolRuleId: string
  sourceKey: string
  positionIndex: number
  groupKey: CrmGroupKey
  name: string
  laneKey: CrmLaneKey
  bar: number | null
  catches: string
  why: string
  evaluatorKey: CrmEvaluatorKey
  codeCheckKey: string | null
  draftModeKey: CrmDraftModeKey
  templateKindKey: CrmTemplateKind
  templateBody: string
  citedCardId: string | null
  /** Proposed optional column. Invented sample text in the scaffold. */
  exampleMessage: string | null
  updatedAt: string
}

export type CrmBinding = {
  toolRuleBindingId: string
  toolId: string
  toolRuleId: string
  enabled: boolean
  autoSendEnabled: boolean
  bar: number | null
  catches: string | null
  templateBody: string | null
  variablesAsJson: Record<string, string>
  updatedAt: string
}

export type CrmReplySetting = {
  toolReplySettingId: string
  toolId: string | null
  masterAutoSend: boolean
  voiceAsMarkdown: string | null
  bannedWordsAsJson: string[] | null
}

export type CrmCheck = {
  toolRuleId: string
  result: CrmCheckResult
  score: number | null
  ruleUpdatedAt: string
  /** Sample only. One sentence for the How it works trace. */
  note?: string | null
}

export type CrmRun = {
  toolRuleRunId: string
  toolId: string
  inboundCommentId: string
  replyUserId: string | null
  toolRuleId: string | null
  toolRuleBindingId: string | null
  laneKey: CrmLaneKey | null
  score: number | null
  evaluatorKey: CrmEvaluatorKey | null
  modelId: string | null
  probabilitiesAsJson: Record<string, number> | null
  reason: string
  shadow: boolean
  checksAsJson: CrmCheck[]
  filledVariablesAsJson: Record<string, string | null>
  suggestedBody: string
  outcomeKey: CrmOutcomeKey
  draftCommentId: string | null
  draftedAt: string | null
  outboundCommentId: string | null
  sentAt: string | null
  sentOriginKey: 'system' | 'staff' | null
  editedBeforeSend: boolean
  decidedAt: string
  recalledAt: string | null
  operatorLaneKey: 'human' | 'auto' | null
  /** Set when a person marked the message "no reply needed". The run keeps what the rules decided. */
  dismissedAt?: string | null
  dismissedByUserId?: string | null
  dismissReasonKey?: CrmDismissReason | null
  /**
   * The rule the walk matched, set only when a person moved the wording to another rule. `toolRuleId`
   * then names the rule used. Null or absent means the run still uses the rule that matched.
   */
  matchedToolRuleId?: string | null
  draftModeKey?: 'none' | 'pre-drafted' | 'briefing-first' | null
  suggestionSourceKey?: 'stored-template' | 'operator-packet' | 'incomplete' | null
  suggestionSourceLabel?: string | null
  operatorPacket?: string | null
  replyIncomplete?: boolean
}

export type CrmThread = {
  threadId: string
  threadTypeKey: 'direct-chat' | 'tool-chat'
  participantUserIds: string[]
  title: string
  supportHandedOff: boolean
  toolId: string | null
  /** Sample only. */
  channelKey: CrmChannelKey
  /** `Thread` comment count, including comments this sample does not load. */
  commentsCount: number
  startedAt: string
}

/** A file on a message, as the public Chat shows it. `fullUrl` is the download link. */
export type CrmAttachment = {
  storedFileId: string
  title: string
  fullUrl: string
  mimeType: string | null
}

export type CrmComment = {
  commentId: string
  threadId: string
  body: string
  createdAt: string
  createdByUserId: string
  isDraft: boolean
  draftOrigin: CrmDraftOrigin
  withdrawnAt: string | null
  withdrawnByUserId: string | null
  /** Files on the message. Absent means none. */
  files?: CrmAttachment[]
}

export type CrmThreadRead = {
  toolThreadReadId: string
  actorUserId: string
  threadId: string
  openedAt: string
}

export type CrmToolSession = {
  toolSessionId: string
  toolId: string
  userId: string | null
  threadId: string
  title: string
  updatedAt: string
}

export type CrmTestRun = {
  toolTestRunId: string
  startedByUserId: string
  toolId: string | null
  createdAt: string
  /** Null until an operator accepts the batch (L39). A fully right batch is not accepted. */
  acceptedAt: string | null
  acceptedByUserId: string | null
  /**
   * Whether the model scorer answered every message it was needed for (H5). False means the batch
   * ran without the model, so it cannot be accepted. Null is a batch from before this was recorded.
   */
  modelScored: boolean | null
  /** The scorer's model id, when one answered. */
  scorerModelId: string | null
}

export type CrmTestLabel = {
  toolTestLabelId: string
  toolTestRunId: string
  fixtureText: string
  expectedLaneKey: CrmLaneKey
  actualLaneKey: CrmLaneKey | null
  toolRuleId: string | null
  resultKey: 'right' | 'wrong' | 'not-scored'
  fired: boolean
}

export type CrmCard = {
  toolKnowledgeCardId: string
  sourceKey: string
  cardTypeKey: CrmCardTypeKey
  title: string
  /** Sample only (see the note at the top). */
  topic: string
  good: string
  payoff: string
  itemsKey: 'steps' | 'points'
  items: string[]
  failure: string
  link: string | null
  citationKey: string | null
  /** The library rules that cite this card. Clearing the link blocks auto-send for each of them. */
  citedBy: CrmCardCitation[]
  /** Sample only. The API has no such column and sends null, which hides the chip in the list. */
  status: 'decided' | 'open' | null
  updatedAt: string
}

export type CrmCardCitation = { toolRuleId: string; sourceKey: string | null; name: string }

export type CrmStudioRevision = {
  toolStudioRevisionId: string
  actorUserId: string
  toolSessionId: string | null
  targetKey: 'rule' | 'binding' | 'tool-copy'
  targetId: string
  beforeAsJson: unknown
  afterAsJson: unknown
  undoneAt: string | null
  createdAt: string
}

export type CrmAudit = {
  toolMessageAuditId: string
  commentId: string
  actorUserId: string
  visibleAuthorUserId: string
  originKey: 'staff' | 'system' | 'self'
  toolRuleId: string | null
  toolRuleBindingId: string | null
  toolRuleRunId: string | null
  recalledAt: string | null
  recalledByUserId: string | null
}

/**
 * A scored message with a winning rule, next to what went out first. The API reads this from
 * `scoToolRuleRuns`: "what happened" is the first reply in the thread (a person, a template, or the
 * AI's own answer), and "what we'd do" is the run's suggested body. The sample set is a small
 * set of written examples with every field filled in.
 */
export type CrmReplay = {
  replayId: string
  toolId: string
  title: string
  ruleSourceKey: string | null
  /** The lane the walker chose, after any operator move. Null in the sample set, where the rule sets it. */
  laneKey: CrmLaneKey | null
  occurredAt: string
  memberMessage: string
  /** Null means nobody has answered yet. */
  actualReply: string | null
  /** Null when there is no reply, so there is no wait to show. */
  actualDelayHours: number | null
  /** Empty for a hard stop, a "no reply" lane, or a rule with no template. */
  wouldReply: string
  /** Null when the server cannot say how long the decision took. */
  wouldDelaySeconds: number | null
  /** The walker's reason. Empty hides the callout. */
  note: string
}

export type CrmStore = {
  users: CrmUser[]
  tools: CrmTool[]
  rules: CrmRule[]
  bindings: CrmBinding[]
  replySettings: CrmReplySetting[]
  runs: CrmRun[]
  threads: CrmThread[]
  comments: CrmComment[]
  threadReads: CrmThreadRead[]
  sessions: CrmToolSession[]
  testRuns: CrmTestRun[]
  testLabels: CrmTestLabel[]
  cards: CrmCard[]
  revisions: CrmStudioRevision[]
  audits: CrmAudit[]
  replays: CrmReplay[]
}

export type CrmPage<T> = {
  items: T[]
  pagination: {
    page: number
    pageSize: number
    itemCount: number
    pageCount: number
    hasNextPage: boolean
  }
}

export type CrmTextSource = 'library' | 'binding'

export type CrmRuleView = {
  rule: CrmRule
  binding: CrmBinding | null
  effectiveCatches: string
  catchesSource: CrmTextSource
  effectiveTemplate: string
  templateSource: CrmTextSource
  variableKeys: string[]
  recordKeys: string[]
  variables: Record<string, string>
  enabled: boolean
  autoSendBlockedReason: string | null
  effectiveBar: number | null
  barSource: CrmTextSource
  /**
   * Matching messages for this tool in the sample history. The server does not fill this in (it is 0
   * on the library read), so the template list and the rule editor read `ruleUsage` instead.
   */
  usedCount: number
  /** How many accounts have this rule switched on. A shared edit changes every one that has not written its own. */
  boundCount: number
}

/** What the template detail shows besides the wording: member messages and how the template does. */
export type CrmTemplateDetail = {
  /** Up to three messages members sent that this rule caught. Plain text, never markdown. */
  messages: string[]
  matchedCount: number
  /** Share of all this account's messages this rule caught. */
  sharePct: number
  sentCount: number
  /** Of the drafts that went out, how many a person changed first. Null when none went out. */
  editedPct: number | null
}

export type CrmAccountRow = CrmUser & {
  responderTitle: string | null
}

/** needs = a person has to act; unchecked = no run yet; handled = nothing to do. */
export type CrmQueueBucket = 'needs' | 'unchecked' | 'handled'

/** What the pill at the end of a queue row says. */
export type CrmVerdictKind =
  'human' | 'assist' | 'auto' | 'none' | 'support' | 'unchecked' | 'unsent' | 'dismissed'

/** Which small flag sits on a conversation in the list. */
export type CrmFlagKind = 'late' | 'needs' | 'ai' | 'none'

export type CrmThreadRow = {
  threadId: string
  title: string
  memberUserId: string | null
  /** The other person in the thread, the one who is not the selected account. */
  memberDisplayName: string
  channelKey: CrmChannelKey
  commentsCount: number
  startedAt: string
  preview: string
  /** Files on the message the preview shows. A message that is only a file has an empty preview. */
  latestFileCount: number
  lastMessageAt: string
  /**
   * Minutes since the message that sets the wait: the member's latest message while a reply is owed,
   * otherwise the latest message in the conversation. Against the sample clock in the sample client.
   */
  ageMinutes: number
  /**
   * Whether the member wrote the latest message. False means the account did, so the next move is
   * theirs. Null when the API cannot say, and the screens then do not claim either.
   */
  lastFromMember: boolean | null
  laneKey: CrmLaneKey | null
  ruleName: string | null
  ruleSourceKey: string | null
  supportHandedOff: boolean
  /** The member's latest message. Clearing a message names it, so a newer one is never hidden. Null hides the action. */
  latestInboundCommentId: string | null
  /** A person marked the latest message "no reply needed". */
  dismissed: boolean
  dismissedByName: string | null
  dismissReasonKey: CrmDismissReason | null
  bucket: CrmQueueBucket
  verdictKind: CrmVerdictKind
  verdictLabel: string
  /** First sentence of the run's reason, for the orange line under the name. Null hides the line. */
  owed: string | null
  /**
   * Whether a tool draft is waiting. Null means the API cannot say from the queue payload, and the
   * screens hide every line that depends on it instead of claiming "no draft".
   */
  hasDraft: boolean | null
  /** The latest run's outcome. Null when no rule has looked at the thread. */
  outcomeKey: CrmOutcomeKey | null
  flag: CrmFlagKind
}

export type CrmSessionRow = {
  toolSessionId: string
  threadId: string
  title: string
  toolTitle: string
  userId: string | null
  userLabel: string
  updatedAt: string
  /** Minutes since the last message, against the sample clock. */
  ageMinutes: number
}

export type CrmCommentRow = CrmComment & {
  senderName: string
  isAutoReply: boolean
  canUnsend: boolean
  /** in = from the member. out = from the account or a tool persona. */
  direction: 'in' | 'out'
}

/** Phase 3. `toolDrafts` only. Personal drafts are a different query and never land here. */
export type CrmToolDraftRow = {
  commentId: string
  threadId: string
  body: string
  createdByUserId: string
  createdAt: string
  updatedAt?: string | null
}

export type CrmMessageOrigin = 'staff' | 'system' | null

/** Who "Send as" names. `person` is true when the visible author is a real person, not a tool persona. */
export type CrmSendAs = {
  name: string | null
  blockedReason: string | null
  person: boolean
}

/**
 * One published comment from scoCrmThread. `autoReplyLabel` is derived from originKey system.
 * It is not a stored column. `displayBody` is the bubble text, including the unsent sentence.
 */
export type CrmTranscriptComment = {
  commentId: string
  body: string
  createdByUserId: string
  createdAt: string
  withdrawnAt: string | null
  withdrawnByUserId: string | null
  originKey: CrmMessageOrigin
  senderName: string
  isAutoReply: boolean
  /** The exact label `auto-reply`, or null for a hand send (staff or null) and for an unsent bubble. */
  autoReplyLabel: string | null
  /** Body, or "This message was unsent." when withdrawnAt is set. */
  displayBody: string
  canUnsend: boolean
  direction: 'in' | 'out'
  /** Files on the message. Never any on an unsent one. */
  files: CrmAttachment[]
}

export type CrmThreadTranscript = {
  threadId: string
  threadTypeKey?: string | null
  comments: CrmTranscriptComment[]
  /** Latest tool_rule_runs row for this thread. Null until a walker has seen it. */
  run: CrmRunView | null
  ownerUserId?: string | null
  ownerDisplayName?: string | null
  ownedByViewer?: boolean
  claimExpiresAt?: string | null
  refreshedAt?: string | null
  latestInboundCommentId?: string | null
  supportOpen?: boolean
  supportTicketId?: string | null
}

/** upsertToolDraft. commentId is the screen's open draft. The API input has no commentId. */
export type CrmToolDraftWrite = {
  threadId: string
  toolId: string
  body: string
  commentId?: string | null
  /** The member message this draft answers, when the screen knows it. */
  inboundCommentId?: string | null
}

/** discardToolDraft. Clears the open tool draft and the run's suggested reply. */
export type CrmDiscardDraftWrite = {
  threadId: string
  toolId: string
  /** The member message the suggestion answers, when the screen knows it. */
  inboundCommentId?: string | null
}

/** sendToolMessage. draftCommentId is the open tool draft. The API field is commentId. */
export type CrmToolSendWrite = {
  threadId: string
  toolId: string
  body: string
  draftCommentId?: string | null
  commentId?: string | null
  inboundCommentId?: string | null
  /** The latest member message the desk was showing. */
  seenInboundCommentId?: string | null
}

export type CrmRunView = CrmRun & {
  dismissedByName?: string | null
  /** Name of the rule the walk matched, when the wording was moved to another rule. */
  matchedRuleName?: string | null
  winnerName: string | null
  winnerSourceKey: string | null
  checks: Array<CrmCheck & { name: string }>
  missingKeys: string[]
}

export type CrmReadinessRule = {
  toolRuleId: string
  sourceKey: string | null
  name: string
  statusKey: 'ready' | 'blocked'
  blockers: string[]
  explanation: string
  visibleSenderName: string
  finalWording: string | null
}

export type CrmReadiness = {
  toolId: string
  visibleSenderName: string
  rules: CrmReadinessRule[]
}

export type CrmFact = { label: string; value: string; tone?: 'green' | 'orange' | 'yellow' | 'purple' }

export type CrmActivityItem = {
  key: string
  /** `**bold**` marks the lead phrase. */
  title: string
  meta: string
  hot: boolean
}

export type CrmMemberView = {
  userId: string
  displayName: string
  headline: string | null
  email: string
  crmEnabled: boolean
  responderTitle: string | null
  samplePlanName: string | null
  samplePriceLabel: string | null
  memberSince: string | null
  /** Small uppercase tags under the name. */
  tags: string[]
  summary: string | null
  /** `stored` is tool_member_summaries.body. Omitted is the sample sentence. */
  summaryKind?: 'sample' | 'stored'
  summaryGeneratedAt?: string | null
  summaryAccountName?: string | null
}

export type CrmThreadDetail = {
  thread: CrmThread
  nowIso: string
  member: CrmMemberView | null
  comments: CrmCommentRow[]
  drafts: CrmCommentRow[]
  run: CrmRunView | null
  bookCallHref: string | null
  readCount: number
  /** The line in the header pill: Needs your approval, AI handled this, and so on. */
  statusLabel: string
  statusTone: 'yellow' | 'red' | 'green' | 'gray' | 'purple'
  /** The small note above the composer: what is off, or how it went out. */
  composerNote: string
  composerNoteTone: 'lock' | 'sent'
  /** How the composer opens. */
  composerMode: 'draft' | 'sent' | 'blank'
  facts: CrmFact[]
  /** Recent messages. Null when the API does not supply them, so the pane hides the section. */
  activity: CrmActivityItem[] | null
  ownerUserId?: string | null
  ownerDisplayName?: string | null
  ownedByViewer?: boolean
  refreshedAt?: string | null
  latestInboundCommentId?: string | null
  supportOpen?: boolean
  supportTicketId?: string | null
}

export type CrmMemberRecord = CrmMemberView & {
  numbers: CrmFact[]
  /** Every interaction. Null when the API does not supply it, so the screen hides the section. */
  activity: CrmActivityItem[] | null
  /**
   * What is waiting on the operator. Null means nothing is. Omitted means the live record cannot
   * say, and the screen hides the block instead of claiming "Nothing is waiting on you".
   */
  openItem?: {
    threadId: string
    kicker: string
    title: string
    meta: string
    actionLabel: string
  } | null
  toldBefore: Array<{ quote: string; by: string; when: string }>
  account: CrmFact[]
  /** The conversation this person most recently wrote in, for "Open their conversation". */
  latestThreadId: string | null
  /** True on an API member record, so the desk can refresh HubSpot. Absent on the sample record. */
  hubspotReadable?: boolean
  /** Stored HubSpot snapshot. Absent on the sample record. The read does not call HubSpot. */
  hubspotContactId?: string | null
  hubspotPortalUrl?: string | null
  hubspotUnavailableReason?: string | null
  hubspotFetchedAt?: string | null
  hubspotItems?: Array<{ type: string; at: string; title: string }>
}

export type CrmVolumeRow = {
  label: string
  laneKey: CrmLaneKey | null
  count: number
  note: string
  pct: number
}

export type CrmTemplateHealthRow = {
  sourceKey: string
  name: string
  laneKey: CrmLaneKey
  used: number
  editedPct: number
  verdict: 'trusted' | 'watch' | 'fix'
}

/** One winning rule's counts. A matched-lost rule is not in `sent`. */
export type CrmRuleCount = {
  toolRuleId: string
  sourceKey: string | null
  name: string
  sent: number
  drafted: number
  editedBeforeSend: number
  recalled: number
}

/** Phase 2. Team-advisor inbound volume and median first human reply over the last 90 days. */
export type CrmBaseline = {
  since: string | null
  inboundCount: number
  medianFirstReplySeconds: number | null
  repliedCount: number
}

export type CrmMetrics = {
  toolId: string | null
  toolTitle: string | null
  range: CrmRange
  rangeLabel: string
  messagesIn: number
  messagesInDeltaPct: number | null
  handledCount: number
  handledNoHumanPct: number
  handledNoHumanDeltaPts: number | null
  escalated: number
  escalatedDeltaPct: number | null
  medianFirstReplyMinutes: number | null
  priorMedianFirstReplyMinutes: number | null
  unsent: number
  draftsEditedPct: number
  draftsEditedDeltaPts: number | null
  correctlyCategorizedPct: number | null
  askedForHuman: number
  askedForHumanPct: number
  /**
   * Sample assumption: minutes of staff time one handled message would have cost.
   * Null on the live report, which does not estimate staff hours.
   */
  minutesPerMessage: number | null
  staffHours: number
  waitedOver: { hours: number; count: number; topLabel: string | null }
  volumeHandled: CrmVolumeRow[]
  volumeEscalated: CrmVolumeRow[]
  templateHealth: CrmTemplateHealthRow[]
  /** Sample prose, or the server's count sentence. The label says which. */
  narrative: string | null
  /** `counts` is the live report. `sample` keeps the fixture paragraph and staff hours. */
  narrativeSource: 'sample' | 'counts'
  ruleCounts: CrmRuleCount[]
  sent: number
  drafted: number
  editedBeforeSend: number
  held: number
  silent: number
  /** Messages a person marked "no reply needed". Not counted as held or silent. */
  dismissed: number
  automatedReplies: number
  staffReplies: number
  whatIfCount: number | null
}

export type CrmTestRunView = CrmTestRun & {
  labels: CrmTestLabel[]
  fired: number
  right: number
  wrong: number
  notScored: number
}

export type CrmHowTrace = {
  memberName: string
  memberLine: string
  sentAtLabel: string
  message: string
  ruleName: string
  /** 1-based position in the ordered library. 0 when the rule is no longer in it. */
  ruleIndex: number
  ruleCount: number
  bar: number | null
  score: number | null
  laneKey: CrmLaneKey
  stopsRan: number
  notChosen: string | null
  pulled: Array<{ key: string; value: string }>
  /** Null hides the "Checked" line. The API does not store what a lookup was checked against. */
  checked: string | null
  templateName: string
  filled: string
  sentAs: string
  /** What happened to the reply. The sample trace is a sent one. */
  outcomeKey: CrmOutcomeKey
  sentSeconds: number | null
  usedToDo: string | null
}

/** dismissCrmThread. `seenInboundCommentId` is the member message the screen was showing. */
export type CrmDismissWrite = {
  threadId: string
  accountUserId: string
  seenInboundCommentId: string
  reasonKey?: CrmDismissReason | null
}

/** regenerateCrmReply. `toolRuleId` is the rule whose wording to use. Leave it out to use the run's own rule. */
export type CrmRegenerateWrite = {
  threadId: string
  accountUserId: string
  seenInboundCommentId: string
  toolRuleId?: string | null
}

/** generateCrmReply. `scope` is the latest sent message, or the sent conversation. Nothing is sent. */
export type CrmGenerateScope = 'last' | 'thread'

export type CrmGenerateWrite = {
  threadId: string
  accountUserId: string
  scope: CrmGenerateScope
  includeFiles: boolean
  instruction?: string | null
  /** A chat tool to draft through. Empty runs this account's reply tool as its own AiAction. */
  toolId?: string | null
}

/** What a redraft wrote, so the composer can show it before the thread has been read again. */
export type CrmRedraft = {
  suggestedBody: string
  toolRuleId: string
  changed: boolean
  /** Fill-ins the rule has no value for. They stay as {{key}} until a person replaces them. */
  missingKeys: string[]
}

export type CrmClientError = { ok: false; error: string }
export type CrmClientOk = { ok: true; id?: string; redraft?: CrmRedraft; generated?: string }
export type CrmClientResult = CrmClientOk | CrmClientError

export function crmError(result: CrmClientResult): string | null {
  if (result.ok === false) {
    return result.error
  }
  return null
}

/** The automatic reply the composer shows once it has sent: the newest one that has not been unsent. */
export function sentAutoReply(detail: Pick<CrmThreadDetail, 'comments'>): CrmCommentRow | null {
  return [...detail.comments].reverse().find(comment => comment.isAutoReply && !comment.withdrawnAt) || null
}

export type CrmListQuery = {
  page: number
  pageSize: number
  sortField: 'priority' | 'lastMessageAt' | 'title' | 'updatedAt'
  sortDirection: 'asc' | 'desc'
  keyword?: string
}

export type CrmBindingInput = {
  toolId: string
  toolRuleId: string
  /** null means use the shared wording. undefined leaves the field as it is. */
  catches?: string | null
  templateBody?: string | null
  variablesAsJson?: Record<string, string>
  bar?: number | null
  enabled?: boolean
  autoSendEnabled?: boolean
  confirmHardStop?: boolean
}

/** A shared-rule edit (`upsertToolRule`). Lane and bar are refused on a hard stop. */
export type CrmRulePatch = {
  name?: string
  catches?: string
  why?: string
  laneKey?: CrmLaneKey
  bar?: number | null
  templateBody?: string
  templateKindKey?: CrmTemplateKind
  exampleMessage?: string | null
}

export type CrmCardPatch = Partial<
  Pick<
    CrmCard,
    'title' | 'cardTypeKey' | 'topic' | 'good' | 'payoff' | 'itemsKey' | 'items' | 'failure' | 'link'
  >
>

/** setToolServes. servesUserId: omitted leaves the person, null clears. consent: omitted leaves it. */
export type CrmToolServesInput = {
  toolId: string
  servesUserId?: string | null
  consent?: boolean
}

/**
 * The two automatic-sending switches. `globalOn` is the row with no tool. `toolOn` is this tool's
 * row. A missing row is off. Both have to be on before a rule can send on its own.
 */
export type CrmReplyAutoSend = {
  globalOn: boolean
  toolOn: boolean
}

/** setToolReplySettings for one switch. `toolId` null is every reply tool. */
export type CrmReplyAutoSendInput = {
  toolId: string | null
  masterAutoSend: boolean
}

/** How many runs each rule won for one tool, by toolRuleId. A rule that won nothing is absent. */
export type CrmRuleUsage = Record<string, number>

export const RECORD_VARIABLE_KEYS = ['first_name', 'sla_date', 'resource_links'] as const

export const SAMPLE_OPERATOR_ID = 'user-sample-operator'

/**
 * Jev scores a rule from 0 to 100. These bars are the auto-send thresholds.
 * A score under the bar and at least 50 still matches and is held for a person.
 * 65 and 50 both auto-send once the score reaches them.
 */
export const CONFIDENCE_STEPS = [
  { key: '95', label: '95%', bar: 95, hint: 'Auto-sends at 95% or higher.' },
  { key: '85', label: '85%', bar: 85, hint: 'Auto-sends at 85% or higher.' },
  { key: '75', label: '75%', bar: 75, hint: 'Auto-sends at 75% or higher.' },
  { key: '65', label: '65%', bar: 65, hint: 'Auto-sends at 65% or higher.' },
  { key: '50', label: '50%', bar: 50, hint: 'Auto-sends at 50% or higher.' },
] as const

/** The step whose number is nearest the stored bar. An equal distance keeps the higher step. */
export function confidenceStepFor(bar: number | null | undefined) {
  if (bar == null) {
    return null
  }
  return CONFIDENCE_STEPS.reduce((best, step) =>
    Math.abs(step.bar - bar) < Math.abs(best.bar - bar) ? step : best
  )
}
